import React, { useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { useHistory } from "react-router";
import { CREATOR_AND_UP, GUEST } from "common/authorization";
import { Attachment } from "common/utils/customObjects";
import { isNotNil } from "common/utils/tools";
import { AuthContext } from "../../../../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../../Common/__styles__/Modal";
import { Button } from "../../../../Common/Buttons/Button";
import Modal from "../../../../Common/Modal";
import { Label } from "../../../../Inputs";
import { Required } from "../../../../Inputs/__styles__/Label";
import { Select, Text } from "../../../../Inputs/react-hook-form";
import { SUBMISSION_CATEGORY_OPTIONS } from "../../../../Submissions/utils";
import { InputGrid } from "../../PropertyData/__styles__/floodInfoModals";
import { RequiredMessage } from "../../PropertyData/__styles__/upsertDfeRuleModal";
import {
  SubmissionsBuilderContext,
  SubmissionsBuilderContextInstance,
} from "./context";
import { RoleAndGroupSelector } from "./RoleAndGroupSelector";
import AttachmentSelect from "./SidebarColumn/AttachmentSelect";
import { SubmissionsBuilderFormDataStructure } from "./types";

export type CreateSubmissionTypeFormStructure = Pick<
  SubmissionsBuilderFormDataStructure,
  "name" | "category" | "canCreateRolesAndGroupIds" | "attachments"
>;

type CreateSubmissionTypeModalProps = {
  onCancel: () => void;
  onSubmit: ({}: CreateSubmissionTypeFormStructure) => void;
  context: SubmissionsBuilderContext;
  usePortalContainerRef?: typeof useRef;
};

export const useCreateSubmissionTypeModal = ({
  onSubmit,
  context,
}: Pick<CreateSubmissionTypeModalProps, "onSubmit" | "context">) => {
  const history = useHistory();
  const [show, hide] = useModal(
    () => (
      <Modal>
        <CreateSubmissionTypeModal
          onCancel={() => history.push("/settings/account/records")}
          onSubmit={(values: CreateSubmissionTypeFormStructure) => {
            onSubmit(values);
            hide();
          }}
          context={context}
        />
      </Modal>
    ),
    [context, onSubmit],
  );

  return [show, hide] as const;
};

export const CreateSubmissionTypeModal = ({
  onCancel,
  onSubmit,
  context,
}: CreateSubmissionTypeModalProps) => {
  // We're using a new form here, because we want a subset of the fields and to have the nicety of things like errors and submit button disabling
  const formMethods = useForm<CreateSubmissionTypeFormStructure>();
  const { account } = useContext(AuthContext);
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = formMethods;

  const [portalContainerRef, setPortalContainerRef] =
    useState<HTMLDivElement | null>(null);

  // we don't get JSON schema validation for free, so have to handroll
  useEffect(() => {
    register("attachments", {
      validate: {
        minLength: (value: Maybe<Attachment[]>) =>
          value && value.length > 0 ? true : "must NOT have fewer than 1 items",
      },
    });
  });

  const handleSubmitCallback = (data: CreateSubmissionTypeFormStructure) =>
    onSubmit(data);

  return (
    <Container overflows>
      <HeaderSection>
        <h1>Add record type</h1>
      </HeaderSection>
      <SubmissionsBuilderContextInstance.Provider value={context}>
        <FormProvider {...formMethods}>
          <FormSection overflows>
            <ContentSection overflows>
              <RequiredMessage>
                All fields are required<Required>*</Required>
              </RequiredMessage>

              <InputGrid
                style={{ marginTop: "24px" }}
                ref={setPortalContainerRef}
              >
                <Text
                  label="Record type"
                  error={errors.name?.message}
                  {...register("name", { required: "Record type is required" })}
                />

                <div>
                  <Label text="Record category" htmlFor="category" />
                  <Select
                    control={control}
                    name="category"
                    options={SUBMISSION_CATEGORY_OPTIONS}
                    size="medium"
                    required={true}
                    error={errors.category?.message}
                    rules={{ required: "Record category is required" }}
                  />
                </div>
                <RoleAndGroupSelector
                  control={control}
                  label="Who can fill out this form?"
                  portalContainerRef={portalContainerRef}
                  name="canCreateRolesAndGroupIds"
                  options={{
                    roles: [...CREATOR_AND_UP, ...GUEST],
                    userGroups: account?.userGroups.filter(isNotNil) ?? [],
                  }}
                  rules={{ required: "This field is required" }}
                  error={errors.canCreateRolesAndGroupIds?.message}
                />
                <AttachmentSelect disabled={false} />
              </InputGrid>
            </ContentSection>
          </FormSection>
        </FormProvider>
      </SubmissionsBuilderContextInstance.Provider>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            size="medium"
            variant="secondary"
            onPress={onCancel}
            title="Cancel"
          />
          <Button
            size="medium"
            variant="primary"
            onPress={() => handleSubmit(handleSubmitCallback)()}
            isDisabled={!isValid}
            data-testid="add-submission-type-button"
            title="Add"
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
};
